import {
    useEffect, useMemo, useRef, useState,
} from 'react';
import Supercluster from 'supercluster';

import useMapViewport from './useMapViewport';

interface MarkerInput {
    id?: string;
    lat: number;
    lng: number;
    name: string;
    isMyShop: boolean;
}

interface PointProperties {
    id: string;
    name: string;
    isMyShop: boolean;
    cluster: false;
}

type ClusterFeature = Supercluster.ClusterFeature<{ point_count: number; cluster: true }>;
type PointFeature = Supercluster.PointFeature<PointProperties>;
type AnyFeature = ClusterFeature | PointFeature;

function useSupercluster(markers: MarkerInput[] | undefined) {
    const { bbox, zoom } = useMapViewport({ padding: 100 });
    const [version, setVersion] = useState(0);

    const supercluster = useMemo(
        () => new Supercluster<PointProperties>({
            radius: 60,
            maxZoom: 16,
            extent: 256,
        }),
        [],
    );

    const prevMarkersRef = useRef<MarkerInput[] | undefined>(undefined);

    useEffect(() => {
        if (prevMarkersRef.current === markers) return;
        prevMarkersRef.current = markers;

        const points: PointFeature[] = (markers ?? []).map((m) => ({
            type: 'Feature' as const,
            geometry: {
                type: 'Point' as const,
                coordinates: [m.lng, m.lat],
            },
            properties: {
                cluster: false as const,
                id: m.id ?? '',
                name: m.name,
                isMyShop: m.isMyShop,
            },
        }));

        supercluster.load(points);
        setVersion((v) => v + 1);
    }, [markers, supercluster]);

    const clusters = useMemo<AnyFeature[]>(() => {
        if (version === 0 && !markers?.length) return [];
        try {
            return supercluster.getClusters(bbox, zoom) as AnyFeature[];
        } catch {
            return [];
        }
    }, [supercluster, bbox, zoom, version]);

    return { clusters, supercluster };
}

export default useSupercluster;
export type { AnyFeature, ClusterFeature, PointFeature };
